import { Procedure } from "@/backend/models/procedure";
import { ProcedureVersion } from "@/backend/models/procedureVersion";
import { ProcedureInstance } from "@/backend/models/procedureInstances/procedureInstance";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { ProcedureController } from "../controllers/procedureController";
import { ProcedureVersionController } from "../controllers/procedureVersionController";
import { ProcedureParams } from "@/backend/access/procedure/types";
import { ProcedureCreateRequest, ProcedureDuplicateRequest } from "@/backend/express/controllers/procedureController";
import { VersionType } from "@/types/genericVersionTypes";
import { FusaReply } from "@/types/replyTypes";

export type ProcedureStore = {
  procedures: Procedure[];
  procedureVersions: ProcedureVersion[];
  procedureInstances: ProcedureInstance[];
  reset: () => void;
  setProcedures: (procedures: Procedure[]) => void;
  fetchProcedures: () => Promise<void>;
  createProcedure: (request: ProcedureCreateRequest) => Promise<void>;
  deleteProcedure: (id: string) => Promise<void>;
  duplicateProcedure: (id: string, request: ProcedureDuplicateRequest) => Promise<FusaReply>;
  fetchVersionsForProcedure: (procedureId: string) => Promise<ProcedureVersion[]>;
  readProcedure: (id: string) => Promise<void>;
  updateProcedure: (procedure: ProcedureParams) => Promise<void>;
  createNewVersion: (id: string) => Promise<FusaReply | undefined>;
  deleteProcedureVersion: (id: string) => Promise<FusaReply | undefined>;
  publishProcedureVersion: (versionId: string, procedureId: string, versionType: VersionType) => Promise<void>;
  fetchVersion: (id: string) => Promise<ProcedureVersion | undefined>;
  updateViewedAt: (id: string) => Promise<void>;
};

const initialState = {
  procedures: [] as Procedure[],
  procedureVersions: [] as ProcedureVersion[],
  procedureInstances: [] as ProcedureInstance[],
};

export const useProcedureStore = create<ProcedureStore>()(immer((set, get) => ({
  ...initialState,
  reset: () => {
    set(initialState);
  },
  setProcedures: (procedures: ProcedureParams[]) => {
    set({ procedures: procedures.map((procedure) => new Procedure(procedure)) });
  },
  fetchProcedures: async () => {
    const response = await new ProcedureController().readAll();
    if (response.errors) return;
    get().setProcedures(response.procedures);
  },
  createProcedure: async (request: ProcedureCreateRequest) => {
    const response = await new ProcedureController().create(request);
    if (response.errors) return;
    const { procedures, setProcedures } = get();
    setProcedures([...procedures, response.procedure]);
  },
  deleteProcedure: async (id: string) => {
    const response = await new ProcedureController().delete(id);
    if (response.errors) return;
    const { procedures, setProcedures } = get();
    setProcedures(procedures.filter((procedure) => procedure.id !== id));
  },
  duplicateProcedure: async (id: string, request: ProcedureDuplicateRequest) => {
    const response = await new ProcedureController().duplicate(id, request);
    if (response.errors) return response;
    const { procedures, setProcedures } = get();
    setProcedures([response.procedure, ...procedures]);
    return response;
  },
  fetchVersionsForProcedure: async (procedureId: string) => {
    const response = await new ProcedureVersionController().readAllByProcedureId(procedureId);
    if (response.errors) return [];
    return response.procedureVersions.map((version) => new ProcedureVersion(version));
  },
  readProcedure: async (id: string) => {
    const response = await new ProcedureController().read(id);
    if (response.errors) return;
    set({
      procedures: [new Procedure(response.procedure)],
      procedureVersions: response.versions.map((version) => new ProcedureVersion(version)),
      procedureInstances: response.instances.map((instance) => new ProcedureInstance(instance)),
    });
  },
  updateProcedure: async (procedure: ProcedureParams) => {
    const response = await new ProcedureController().update({ procedure });
    if (response.errors) return;
    const { procedures } = get();
    set({
      procedures: procedures.map((p) =>
        p.id === response.procedure?.id ? new Procedure(response.procedure) : p
      ),
    });
  },
  createNewVersion: async (id: string) => {
    const response = await new ProcedureController().createNewVersion(id);
    if (response.errors) return response;
    set({
      procedureVersions: [new ProcedureVersion(response.version), ...get().procedureVersions],
      procedureInstances: [new ProcedureInstance(response.instance), ...get().procedureInstances],
    });
  },
  deleteProcedureVersion: async (id: string) => {
    const response = await new ProcedureVersionController().delete(id);
    if (response.errors) return response;
    set({
      procedureVersions: get().procedureVersions.filter((version) => version.id !== id),
      procedureInstances: get().procedureInstances.filter((instance) => instance.versionId !== id),
    });
  },
  publishProcedureVersion: async (versionId: string, procedureId: string, versionType: VersionType) => {
    const response = await new ProcedureVersionController().publish(versionId, procedureId, versionType);
    if (response.errors) return;
    const versionsResponse = await new ProcedureVersionController().readAllByProcedureId(procedureId);
    if (versionsResponse.errors) return;
    set({
      procedureVersions: versionsResponse.procedureVersions.map((v) => new ProcedureVersion(v)),
    });
  },
  fetchVersion: async (id: string) => {
    const response = await new ProcedureVersionController().read(id);
    if (response.errors) return;
    return new ProcedureVersion(response.procedureVersion);
  },
  updateViewedAt: async (id: string) => {
    await new ProcedureController().updateViewedAt(id);
  },
})));